import {MarkGithubIcon, XIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {ReactNode, RefObject} from 'react'
import {useId, useRef} from 'react'

import styles from './Sidebar.module.css'
import {IconButton, useFocusTrap, useProvidedRefOrCreate} from '@primer/react'

/**
 * Discriminated union type for sidebar header props.
 * When using the default header, aria-labelledby is automatically generated.
 * When using a custom header, aria-labelledby is required.
 */
export type SidebarHeaderOverrideProps =
  | {
      /** Uses the default SidebarHeader with GitHub logo and close button. */
      header?: undefined
      'aria-labelledby'?: never
    }
  | {
      /** Custom header element. Pass `null` to hide the header entirely. */
      header: ReactNode | null
      /** ID of the element that labels the sidebar. Required when using a custom header. */
      'aria-labelledby': string
    }

interface SidebarHeaderProps {
  onToggle: () => void
  labelId: string
}

/**
 * Default header for the Sidebar component with GitHub logo and close button.
 */
function SidebarHeader({onToggle, labelId}: SidebarHeaderProps) {
  return (
    <div className={styles.header}>
      <h2 id={labelId} className="sr-only">
        Copilot navigation
      </h2>
      <a href="/" target="_self" className={styles.githubLogo} aria-label="GitHub homepage">
        <MarkGithubIcon size={32} />
      </a>
      <IconButton
        icon={XIcon}
        variant="invisible"
        aria-label="Close sidebar"
        onClick={onToggle}
        className={styles.toggleSidebarFloating}
      />
    </div>
  )
}

interface SidebarBaseProps {
  children: ReactNode
  className?: string
  /**
   * Additional class name to apply to the inner sidebar container div.
   */
  innerClassName?: string
  isVisible: boolean
  isExpanded: boolean
  isCondensable?: boolean
  sidebarRef?: RefObject<HTMLDivElement | null>
  onToggle: () => void
  'data-testid'?: string
}

type SidebarProps = SidebarBaseProps & SidebarHeaderOverrideProps

/**
 * Generic sidebar component that can be used in various layouts.
 */
export function Sidebar({
  children,
  className,
  innerClassName,
  isVisible,
  isExpanded,
  isCondensable = true,
  sidebarRef,
  onToggle,
  'data-testid': dataTestId,
  header,
  'aria-labelledby': ariaLabelledBy,
}: SidebarProps) {
  const ref = useProvidedRefOrCreate(sidebarRef)
  const contentRef = useRef<HTMLDivElement>(null)
  const generatedId = useId()
  const defaultLabelId = `${generatedId}-copilot-navigation`
  const isDefaultHeader = header === undefined
  const labelId = isDefaultHeader ? defaultLabelId : ariaLabelledBy
  const headerElement = isDefaultHeader ? <SidebarHeader onToggle={onToggle} labelId={defaultLabelId} /> : header

  // Trap focus in the sidebar when it's visible (floating mode)
  // This prevents users from accidentally tabbing out of the panel
  useFocusTrap({
    containerRef: contentRef,
    disabled: !isVisible,
  })

  return (
    <aside
      aria-labelledby={labelId}
      ref={ref}
      className={clsx(styles.container, className, {[styles.visible]: isVisible})}
      data-testid={dataTestId}
    >
      <div ref={contentRef} className={clsx(styles.sidebar, innerClassName)}>
        {headerElement}
        {children}
      </div>
      {isCondensable ? (
        <button
          className={clsx(styles.toggleSidebarVertical, {[styles.isExpanded]: isExpanded})}
          onClick={onToggle}
          // Disable keyboard focus because this is redundant with the visible sidebar buttons
          tabIndex={-1}
        >
          <span className="sr-only">{isVisible || isExpanded ? 'Condense sidebar' : 'Expand sidebar'}</span>
        </button>
      ) : (
        <div className={styles.sidebarDivider} />
      )}
    </aside>
  )
}
